import React from 'react';
import { RefreshControl, SectionList, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import api from '../../../utils/api';
import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';

dayjs.extend(relativeTime);

type Community = { uuid: string; name: string } | null;
type ControlPanel = {
  uuid: string;
  name: string;
  type: string;
  last_ping_at: string | null;
  is_alive: boolean;
  community: Community;
};
type Totem = {
  uuid: string;
  code: string;
  description: string | null;
  type: string;
  community: Community;
};

export default function DevicesScreen() {
  const cpQuery = useQuery<ControlPanel[]>({
    queryKey: ['meta.control-panels'],
    queryFn: () => api.get('/admin/control-panels').then(r => r.data),
  });

  const totemQuery = useQuery<Totem[]>({
    queryKey: ['meta.totems'],
    queryFn: () => api.get('/admin/totems').then(r => r.data),
  });

  const isLoading = cpQuery.isLoading || totemQuery.isLoading;
  const refetch = () => {
    cpQuery.refetch();
    totemQuery.refetch();
  };

  const sections = [
    { title: 'Paneles de control', data: cpQuery.data ?? [], kind: 'panel' as const },
    { title: 'Totems', data: totemQuery.data ?? [], kind: 'totem' as const },
  ];

  return (
    <SafeAreaView style={tw`flex-1 bg-background`}>
      <SectionList
        contentContainerStyle={tw`p-4`}
        sections={sections as any}
        keyExtractor={(item: any) => item.uuid}
        refreshControl={<RefreshControl onRefresh={refetch} refreshing={isLoading} />}
        renderSectionHeader={({ section }) => (
          <Text style={tw`text-gray-700 font-600 mt-3 mb-2`}>{section.title}</Text>
        )}
        renderItem={({ item, section }) => {
          if ((section as any).kind === 'panel') {
            const p = item as ControlPanel;
            return (
              <View style={tw`bg-white rounded p-3 mb-2`}>
                <View style={tw`row justify-between`}>
                  <Text style={tw`text-gray-800 font-500`}>{p.name}</Text>
                  <Text
                    style={tw.style(
                      `text-xs font-600`,
                      p.is_alive ? `text-green-600` : `text-red-500`,
                    )}
                  >
                    {p.is_alive ? '● en línea' : '● sin señal'}
                  </Text>
                </View>
                <Text style={tw`text-gray-500 text-xs`}>
                  {p.community?.name ?? '—'} · {p.type}
                </Text>
                <Text style={tw`text-gray-400 text-xs`}>
                  Última señal: {p.last_ping_at ? dayjs(p.last_ping_at).fromNow() : 'nunca'}
                </Text>
              </View>
            );
          }

          const t = item as Totem;
          return (
            <View style={tw`bg-white rounded p-3 mb-2`}>
              <Text style={tw`text-gray-800 font-500`}>{t.code}</Text>
              <Text style={tw`text-gray-500 text-xs`}>
                {t.community?.name ?? '—'} · {t.type}
              </Text>
              {t.description ? (
                <Text style={tw`text-gray-400 text-xs`}>{t.description}</Text>
              ) : null}
            </View>
          );
        }}
      />
    </SafeAreaView>
  );
}
